import React, { useEffect, useRef, useState } from 'react';
import { V } from '../utils/theme';
import { Icons } from './Icons';
import { Btn } from './ui';
import { LinkBtn, LogoMark, Notice, Callout } from './kit';
import { FullSheet, CenterModal } from './FullSheet';
import { ConsentSheet } from './ConsentSheet';
import { applyBrand, appName } from '../utils/brand';
import { orgApiPublic, orgApi, orgErr, errorMessage, personName } from '../utils/org';
import { LS } from '../utils/storage';

const PREVIEW_CACHE = "ft-pending-join-preview";

// Branded landing for ?join=CODE. Public (no session): preview_invite gives the
// org name, logo, colours and coach. Applies the preview branding so the
// onboarding that follows is already in the gym's colours.
//   hasAccount: the user is onboarded + signed in → "Join now" redeems directly
export function JoinLanding({ code, hasAccount, onProceed, onCancel }) {
  const [preview, setPreview] = useState(() => { const c = LS.get(PREVIEW_CACHE); return c && c.code === code ? c.preview : null; });
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(!preview);

  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const p = await orgApiPublic('preview_invite', { code });
        if (dead) return;
        setPreview(p); setLoading(false);
        LS.set(PREVIEW_CACHE, { code, preview: p });
        if (p && p.app_name) applyBrand(p);
      } catch (e) {
        if (dead) return;
        const er = orgErr(e);
        if (er.status === 0 && preview) { setLoading(false); return; } // offline: keep cached preview
        setErr(er); setLoading(false);
      }
    })();
    return () => { dead = true; };
  }, [code]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (preview && preview.app_name && !V.brand) applyBrand(preview); }, [preview]);

  const cancel = () => { LS.del(PREVIEW_CACHE); applyBrand(null); onCancel(); };
  const name = preview?.org_name || "your gym";
  const app = preview?.app_name || appName();
  const coach = preview?.coach_name;
  const isCoachInvite = preview?.role === 'coach';

  return (
    <FullSheet ariaLabel={`Join ${name}`} zIndex={9991}
      footer={
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {err ? (
            <Btn full onClick={cancel} s={{ minHeight: 50 }}>Continue without joining</Btn>
          ) : hasAccount ? (
            <Btn full disabled={loading} onClick={() => onProceed('redeem')} s={{ minHeight: 50, fontSize: 15 }}>Join {name}</Btn>
          ) : (<>
            <Btn full disabled={loading} onClick={() => onProceed('signup')} s={{ minHeight: 50, fontSize: 15 }}>Create account and join</Btn>
            <Btn v="secondary" full disabled={loading} onClick={() => onProceed('signin')} s={{ minHeight: 48, color: V.text }}>I already have an account</Btn>
          </>)}
          {!err && <div style={{ textAlign: "center" }}><LinkBtn color={V.text3} onClick={cancel}>Not now</LinkBtn></div>}
        </div>
      }>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", paddingTop: 28 }}>
        <LogoMark size={88} />
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".12em", textTransform: "uppercase", color: V.accent, marginTop: 22 }}>You're invited</div>
        <h1 style={{ margin: "8px 0 0", fontSize: 28, fontWeight: 900, letterSpacing: "-0.03em", lineHeight: 1.1, color: V.text }}>
          {loading ? "Loading your invite…" : err ? "This invite can't be used" : <>Join {name}<br />on {app}</>}
        </h1>
        {!loading && !err && (
          <div style={{ fontSize: 14, color: V.text2, lineHeight: 1.55, marginTop: 14, maxWidth: 380 }}>
            {isCoachInvite
              ? `You've been invited to coach at ${name}. Your members' data stays theirs — they choose what to share with you.`
              : coach
                ? <>{coach} will be your coach. Your data stays yours — on the next screen you choose exactly what {coach.split(' ')[0]} can see.</>
                : <>Your data stays yours — on the next screen you choose exactly what your coach can see.</>}
          </div>
        )}
        {err && <Notice tone="error" center style={{ marginTop: 14, fontSize: 13 }}>{errorMessage(err, name)}</Notice>}
        {!loading && !err && !hasAccount && (
          <Callout icon={Icons.download} color={V.accent2} style={{ marginTop: 22, textAlign: "left", width: "100%" }} title="Join first, then install">
            Add {app} to your Home Screen after joining so the icon and name are your gym's.
          </Callout>
        )}
        {!loading && !err && preview?.expires_at && (
          <div style={{ fontSize: 11, color: V.text3, marginTop: 18 }}>Invite valid until {new Date(preview.expires_at).toLocaleDateString()}</div>
        )}
      </div>
    </FullSheet>
  );
}

// Post-session step: redeem the pending code, then show the consent sheet.
// Errors are shown inline (410 expired/exhausted/revoked, 409 already_in_org,
// 403 invite_email_mismatch, 423 org_suspended). `onDone(result|null)` always
// fires once; the caller clears ft-pending-join and refreshes `me`.
export function JoinRedeem({ code, onDone }) {
  const [state, setState] = useState({ phase: 'redeeming' });
  // Retry re-runs the redeem by bumping `attempt`; the effect keys on it.
  const [attempt, setAttempt] = useState(0);
  // `onDone` is an inline-ish prop from App; keep it in a ref so it never
  // re-triggers the redeem effect (and the deps below stay complete).
  const onDoneRef = useRef(onDone);
  useEffect(() => { onDoneRef.current = onDone; });
  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const r = await orgApi('redeem_invite', { code });
        if (dead) return;
        LS.del(PREVIEW_CACHE);
        if (r?.branding) applyBrand(r.branding);
        if (r?.consent_defaults) setState({ phase: 'consent', result: r });
        else { setState({ phase: 'done' }); onDoneRef.current?.(r); }
      } catch (e) {
        if (dead) return;
        setState({ phase: 'error', err: orgErr(e) });
      }
    })();
    return () => { dead = true; };
  }, [code, attempt]);

  if (state.phase === 'consent') {
    const r = state.result;
    const coach = r.coach_email ? { email: r.coach_email } : null;
    return <ConsentSheet orgName={r.org?.name || appName()} coach={coach && { ...coach, name: personName(coach) }} defaults={r.consent_defaults}
      onDone={() => { setState({ phase: 'done' }); onDone(r); }} />;
  }
  if (state.phase === 'error') {
    const er = state.err;
    const retryable = er.status === 0 || er.status === 429 || er.status >= 500;
    return (
      <CenterModal ariaLabel="Could not join">
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
          <LogoMark size={44} />
          <div style={{ fontSize: 17, fontWeight: 800, color: V.text }}>Couldn't join {er.org_name ? "" : "the gym"}</div>
        </div>
        <div style={{ fontSize: 13, color: V.text2, lineHeight: 1.55 }}>{errorMessage(er)}</div>
        <div style={{ display: "flex", gap: 8, marginTop: 18 }}>
          {retryable && <Btn v="secondary" full onClick={() => { setState({ phase: 'redeeming' }); setAttempt(a => a + 1); }}>Retry</Btn>}
          <Btn full onClick={() => { LS.del(PREVIEW_CACHE); onDone(null); }}>OK</Btn>
        </div>
      </CenterModal>
    );
  }
  if (state.phase === 'done') return null;
  return (
    <CenterModal ariaLabel="Joining">
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{ width: 24, height: 24, borderRadius: 12, border: `2px solid ${V.accent}`, borderTopColor: "transparent", animation: "spin 0.8s linear infinite", flexShrink: 0 }} />
        <div style={{ fontSize: 14, fontWeight: 600, color: V.text }}>Joining your gym…</div>
      </div>
    </CenterModal>
  );
}

// Shown once when `me` reports the org is suspended. The app then runs
// unbranded and unflagged; nothing is deleted.
export function SuspendedSheet({ orgName, reason, onContinue }) {
  return (
    <FullSheet ariaLabel="Gym account paused" zIndex={9991}
      footer={<Btn full onClick={onContinue} s={{ minHeight: 50, fontSize: 15 }}>Continue</Btn>}>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", paddingTop: 40 }}>
        <div style={{ width: 72, height: 72, borderRadius: 22, background: `${V.warn}14`, border: `1px solid ${V.warn}30`, display: "flex", alignItems: "center", justifyContent: "center" }}>
          {Icons.pause({ size: 30, color: V.warn })}
        </div>
        <h1 style={{ margin: "22px 0 0", fontSize: 24, fontWeight: 900, letterSpacing: "-0.02em", color: V.text, lineHeight: 1.15 }}>This gym's account is paused.</h1>
        <div style={{ fontSize: 15, color: V.text2, lineHeight: 1.55, marginTop: 12, maxWidth: 360 }}>
          Your data is safe and still yours. {orgName ? `${orgName}'s` : "The gym's"} coaching features are switched off until the account is active again — everything you log keeps working.
        </div>
        {reason && <div style={{ fontSize: 12, color: V.text3, marginTop: 14 }}>Reason given: {reason}</div>}
        <div style={{ fontSize: 12, color: V.text3, marginTop: 18, lineHeight: 1.5 }}>You can leave the gym at any time in Settings → Your gym.</div>
      </div>
    </FullSheet>
  );
}
